import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { CustomOccPrice, CustomProduct, Price, PriceKind } from '@product/const';
import { ProductConditionsService, SelectedPriceService } from '@product/data-access';
import { TransitPriceStateService } from '@product/feature';
import { ProductPipesModule } from '@product/utils';
import { DeviceService } from '@shared/services';
import { CurrentProductService } from '@spartacus/storefront';
import { Observable, filter, map, of, switchMap } from 'rxjs';

@Component({
  selector: 'sd-product-compact-price',
  imports: [ProductPipesModule],
  templateUrl: './product-compact-price.component.html',
  styleUrls: ['./product-compact-price.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductCompactPriceComponent {
  private readonly productConditionsService = inject(ProductConditionsService);
  private readonly currentProductService = inject(CurrentProductService);
  private readonly transitPriceStateService = inject(TransitPriceStateService);
  private readonly selectedPriceService = inject(SelectedPriceService);
  private readonly deviceService = inject(DeviceService);

  readonly product = input<CustomProduct | undefined>(undefined);

  private readonly currentProduct = toSignal(this.currentProductService.getProduct() as Observable<CustomProduct>);

  protected readonly resolvedProduct = computed(() => this.product() ?? this.currentProduct());

  readonly isRentProduct = computed(() => this.resolvedProduct()?.isRent ?? false);

  private readonly isMobileXsDown = toSignal(this.deviceService.isMobileXsDown$, { initialValue: false });
  private readonly isMobileXxsDown = toSignal(this.deviceService.isMobileXxsDown$, { initialValue: false });

  readonly transitInstance = toSignal(
    toObservable(this.resolvedProduct).pipe(
      filter(Boolean),
      switchMap((product) => this.transitPriceStateService.getTransitState(product)),
    ),
  );

  readonly isTransitSelected = toSignal(
    toObservable(this.transitInstance).pipe(switchMap((mode) => mode?.switchState$ ?? of(false))),
    { initialValue: false },
  );

  private readonly prices = toSignal(
    toObservable(this.resolvedProduct).pipe(
      filter(Boolean),
      switchMap((product) => this.selectedPriceService.getPricesBySelectedUnit(product.code)),
    ),
    { initialValue: [] as Price[] },
  );

  private readonly personalPrice = toSignal<CustomOccPrice | undefined>(
    toObservable(this.resolvedProduct).pipe(
      filter(Boolean),
      switchMap((product) =>
        this.selectedPriceService
          .getSelectedPrice(product.code)
          .pipe(map((price) => ({ ...price, productCode: product.code }))),
      ),
      filter(({ unit }) => unit != null),
      switchMap(({ unit, productCode }) =>
        this.productConditionsService.getProductConditions(productCode).pipe(
          filter(Boolean),
          map((conditions) => ({ conditions, unit })),
        ),
      ),
      map(({ conditions, unit }) => {
        if (!unit?.code) return undefined;

        return conditions.find((c) => c.unitCode === unit.code)?.personalPrice;
      }),
    ),
  );

  private readonly priceData = computed(() => {
    const prices = this.prices();
    const personalPrice = this.personalPrice();
    const isTransitSelected = this.isTransitSelected();
    const isMobileXsDown = this.isMobileXsDown();
    const isMobileXxsDown = this.isMobileXxsDown();

    let selectedPrice: CustomOccPrice | undefined;
    let isPersonalPrice = false;
    let secondaryPrice: Price | undefined;

    if (isTransitSelected) {
      selectedPrice = prices.find((p) => p.kind === PriceKind.TRANSIT);
    } else {
      const priceMap = new Map(prices.map((p) => [p.kind, p]));
      const regularPrice = priceMap.get(PriceKind.REGULAR);
      const crossedPrice = priceMap.get(PriceKind.CROSSED);

      if (personalPrice?.value && regularPrice?.value && personalPrice.value <= regularPrice.value) {
        selectedPrice = personalPrice;
        isPersonalPrice = true;
        secondaryPrice = regularPrice;
      } else {
        selectedPrice = regularPrice;
        secondaryPrice = crossedPrice;
      }
    }

    // В связи с договоренностями с дизайнером и бизнесом
    // мы ограничили вывод secondaryPrice по количеству символов
    // и брейкпоинту разрешения экрана пользователя.
    // 1) >16 символов и < 576px
    // 2) >=10 символов и < 375px
    let showSecondaryPrice = false;
    if (selectedPrice && secondaryPrice) {
      const totalLength = (selectedPrice.formattedValue?.length ?? 0) + (secondaryPrice.formattedValue?.length ?? 0);

      showSecondaryPrice = !((isMobileXsDown && totalLength > 16) || (isMobileXxsDown && totalLength >= 10));
    }

    return { price: selectedPrice, isPersonalPrice, secondaryPrice, showSecondaryPrice };
  });

  readonly price = computed(() => this.priceData().price);
  readonly isPersonalPrice = computed(() => this.priceData().isPersonalPrice);
  readonly secondaryPrice = computed(() => this.priceData().secondaryPrice);
  readonly showSecondaryPrice = computed(() => this.priceData().showSecondaryPrice);
}
